import React, { useState, useMemo, useEffect } from 'react'
import styles from './DetailedWorklogsTable.module.css'
import WorklogDescription from './WorklogDescription'

export default function DetailedWorklogsTable({ filters, dateRange, reportData, onPageChange, showBillableColumn = false }) {
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage] = useState(25)
    
    // Use API data or empty array as fallback
    const worklogs = reportData?.data || []
    const mentionableUsers = useMemo(() => {
        const seen = new Map()
        worklogs.forEach(worklog => {
            const user = worklog.user
            if (user?.username && !seen.has(user.username)) {
                seen.set(user.username, user)
            }
        })
        return Array.from(seen.values())
    }, [worklogs])
    const pagination = reportData?.pagination || {
        current_page: 1,
        per_page: 25,
        total_pages: 0,
        total_count: 0
    }

    // Update current page when pagination changes from API
    useEffect(() => {
        if (pagination.current_page !== currentPage) {
            setCurrentPage(pagination.current_page)
        }
    }, [pagination.current_page, currentPage])

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage)
        if (onPageChange) {
            onPageChange(newPage)
        }
    }

    return (
        <div className={styles.detailedView}>
            <div className={styles.worklogsTable}>
                <div className={styles.tableHeader}>
                    <h3>Detailed Worklogs</h3>
                    <div className={styles.tableInfo}>
                        Showing {Math.min((pagination.current_page - 1) * pagination.per_page + 1, pagination.total_count)} - {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of {pagination.total_count} entries
                    </div>
                </div>
                
                <div className={styles.tableContainer}>
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Description</th>
                                <th>Project</th>
                                <th>Task</th>
                                {showBillableColumn && <th>Billable</th>}
                                <th>Time</th>
                                <th className={styles.right}>Duration</th>
                            </tr>
                        </thead>
                        <tbody>
                            {worklogs.length === 0 ? (
                                <tr>
                                    <td colSpan={showBillableColumn ? "7" : "6"} style={{ textAlign: 'center', padding: '2rem' }}>
                                        No worklogs found for the selected criteria.
                                    </td>
                                </tr>
                            ) : (
                                worklogs.map(worklog => (
                                    <tr key={worklog.id}>
                                        <td className={styles.userColumn}>
                                            {worklog.user?.name || 'Unknown User'}
                                        </td>
                                        <td className={styles.descriptionColumn}>
                                            <WorklogDescription description={worklog.description || 'No description'} mentionableUsers={mentionableUsers} />
                                        </td>
                                        <td className={styles.projectColumn}>
                                            {worklog.project?.name || 'No project'}
                                            {worklog.project?.client_name && (
                                                <div style={{ fontSize: '0.8em', color: '#666' }}>
                                                    {worklog.project.client_name}
                                                </div>
                                            )}
                                        </td>
                                        <td className={styles.taskColumn}>
                                            {worklog.task?.name || 'Direct project work'}
                                        </td>
                                        {showBillableColumn && (
                                            <td className={styles.billableColumn}>
                                                <span className={`${styles.billableIndicator} ${worklog.billable ? styles.billableYes : styles.billableNo}`}>
                                                    {worklog.billable ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                        )}
                                        <td className={styles.dateColumn}>
                                            <div>
                                                {new Date(worklog.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} - {new Date(worklog.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                            </div>
                                            <div style={{ fontSize: '0.8em', color: '#666' }}>
                                                {new Date(worklog.date).toLocaleDateString('en-GB')}
                                            </div>
                                        </td>
                                        <td className={styles.hoursColumn}>
                                            {worklog.duration_formatted || '00:00:00'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                
                {/* Pagination */}
                {pagination.total_pages > 1 && (
                    <div className={styles.pagination}>
                        <button 
                            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className={styles.paginationButton}
                        >
                            Previous
                        </button>
                        
                        <div className={styles.paginationInfo}>
                            Page {currentPage} of {pagination.total_pages}
                        </div>
                        
                        <button 
                            onClick={() => handlePageChange(Math.min(pagination.total_pages, currentPage + 1))}
                            disabled={currentPage === pagination.total_pages}
                            className={styles.paginationButton}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
